import { render } from 'preact';
import { useCallback, useEffect, useMemo, useRef, useState, type MutableRef } from 'preact/hooks';
import Banner from '../banner';
import { Confirm, Notification } from '../dialog';
import { getComboBox, setComboBoxSelectedValues } from '../combobox';
import { getInputMask, setInputMask } from '../input-mask';
import {
  debounce,
  EMPTY_OBJECT,
  entriesToObject,
  focusFirstAvailable,
  generateId,
} from '../../utility';
import { useMergedRef } from '../../hooks/useMergedRef';
import type { ComponentChildren, ComponentProps, TargetedEvent } from 'preact';
import type { HTMLAttributes } from '../../types/utility';

type RenderProps = {
  current: Record<string, string | string[]>;
  initial: Record<string, string | string[]>;
  defaultValues: URLSearchParams;
};

type BaseProps = Omit<HTMLAttributes<HTMLFormElement>, 'onSubmit'> & {
  children?: ComponentChildren | ((props: RenderProps) => ComponentChildren);
  onSubmit?: (
    e: TargetedEvent<HTMLFormElement, SubmitEvent>,
  ) => void | Promise<void | { error?: Error | ComponentChildren; message?: ComponentChildren }>;
};

type FormDataResults = ReturnType<typeof getFormState>;

type ResultProps =
  | ({ component: 'Banner' } & Partial<ComponentProps<typeof Banner>>)
  | ({ component: 'Notification' } & Partial<ComponentProps<typeof Notification>>);

type SubmissionState = '' | 'pending' | 'error' | 'success';

interface FormProps extends BaseProps {
  action?: string;
  autoSubmit?: boolean;
  confirmMessage?: Partial<ComponentProps<typeof Confirm>>;
  errorMessage?: ResultProps;
  successMessage?: ResultProps;
  method?: 'get' | 'post' | 'dialog';
  previousState?: {
    message?: string;
    mode: 'auto' | 'confirm';
    storageKey: string;
  };
  urlSynchronization?: 'push' | 'replace';
}

// validate a checkbox list with <fieldset data-required="nameOfField"> has at least one checkbox selected
// HTML5 cannot validate the required attribute like it can with a radio button list
function checkCustomValidity(form: HTMLFormElement, formData: FormData) {
  const fields = Array.from(form.querySelectorAll('[data-required]')) as HTMLElement[];
  return fields.every((field) => {
    const isValid = !!formData.get(field.dataset.required as string);

    field.querySelectorAll('input').forEach((input) => {
      input.setCustomValidity(isValid ? '' : 'Please select a value');
    });

    return isValid;
  });
}

function getMessageType(
  submissionState: SubmissionState,
  successMessage = EMPTY_OBJECT as ResultProps,
  errorMessage = EMPTY_OBJECT as ResultProps,
) {
  let component;
  let props;

  if (submissionState === 'success') {
    ({ component, ...props } = successMessage);
  } else if (submissionState === 'error') {
    ({ component, ...props } = errorMessage);
  }

  return { component, theme: THEME[submissionState as keyof typeof THEME], props };
}

function getDialogContainer() {
  const placeholder = document.getElementById(DIALOG_CONTAINER_ID) || document.createElement('div');

  if (!document.body.contains(placeholder)) {
    placeholder.id = DIALOG_CONTAINER_ID;
    document.body.appendChild(placeholder);
  }

  return placeholder;
}

function synchronizeUrl(form: HTMLFormElement, formData: FormData) {
  if (form.dataset.urlSynchronization === 'replace') {
    const url = new URL(form.action);

    // update url with new form values
    if (form.method === 'get') {
      // @ts-expect-error this is valid per the [URLSearchParams spec](https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams/URLSearchParams#options)
      const updatedSearchParams = new URLSearchParams(formData);

      // set new params from form data
      updatedSearchParams.forEach((value, key) => {
        url.searchParams.append(key, value);
      });
    }

    history.replaceState(null, '', url.toString());
  }
}

const CHANGE_INTERVAL = 1000 / 60; // 60fps = 1000ms / 60
const CONFIRM_EVENTS = ['cancel', 'close'];
const DIALOG_CONTAINER_ID = 'dialog-container';
const FORM_DATA = new WeakMap<SubmitEvent, FormDataResults>();
const FORM_DATA_INITIAL = new WeakMap<
  HTMLFormElement,
  { formData: FormDataResults['formData']; formDataObject: FormDataResults['formDataObject'] }
>();
const THEME = {
  success: 'positive',
  error: 'negative',
} as const;

export default function Form({
  action,
  autoSubmit,
  children,
  confirmMessage,
  errorMessage,
  method = 'get',
  previousState = EMPTY_OBJECT as NonNullable<FormProps['previousState']>,
  successMessage,
  // React 19 delivers `ref` as a regular prop; keep it out of the spread so
  // it can't clobber our internal ref
  ref: forwardedRef,
  urlSynchronization,
  onReset,
  onSubmit,
  ...formProps
}: FormProps) {
  const [submissionState, setSubmissionState] = useState<SubmissionState>('');
  const [key, setKey] = useState(formProps.key || generateId());
  const { handleDismiss: handleDismissConfirm } = Confirm.useConfirm();
  const { handleDismiss: handleDismissNotification } = Notification.useNotification();
  const messageRef = useRef<ComponentChildren>('');
  const formRef = useRef<HTMLFormElement | null>(null);
  const mergedFormRef = useMergedRef(formRef, forwardedRef);

  const handleSubmit = useCallback(
    async (e: TargetedEvent<HTMLFormElement, SubmitEvent>) => {
      const { currentTarget: form } = e;

      setSubmissionState('pending');

      const formData = new FormData(form, e.submitter);
      const isFormValid = checkCustomValidity(form, formData) && form.checkValidity();

      /*
        NOTE: <Dialog> buttons have a legacy name="act" attribute
        it's probably safe to remove the attribute entirely,
        though we'll start with removing the "act" field from serialization
      */
      if (
        form.method === 'dialog' &&
        e.submitter instanceof HTMLButtonElement &&
        e.submitter.name === 'act'
      ) {
        formData.delete('act');
      }

      e.preventDefault();

      // find the first invalid form field and set focus
      if (!isFormValid) {
        focusFirstAvailable(form, {
          selector:
            ':invalid:not(fieldset), .form-field:has(select[name][hidden]:invalid) input[data-name]',
        });
        return;
      }

      if (confirmMessage) {
        render(
          <Confirm
            key={Math.random()}
            onDismiss={handleDismissConfirm}
            // eslint-disable-next-line react/no-children-prop
            children="Are you sure?"
            {...confirmMessage}
            data-force-remove="true"
            open
          />,
          getDialogContainer(),
        );

        const shouldContinue = await new Promise((resolve) => {
          e.preventDefault(); // prevent auto-close if in <dialog>

          // check <dialog> returnValue to decide if onSubmit should be called
          const handler = (e: Event) => {
            const confirm = e.target as HTMLDialogElement;
            const isTrue = confirm.returnValue === 'true';

            CONFIRM_EVENTS.forEach((name) => document.removeEventListener(name, handler, true));

            resolve(isTrue);
          };

          // attach a capture event listening for the dialog close
          CONFIRM_EVENTS.forEach((name) => document.addEventListener(name, handler, true));
        });

        if (!shouldContinue) {
          e.stopImmediatePropagation();
          return;
        }
      }

      FORM_DATA.set(e, getFormState(form, formData));

      synchronizeUrl(form, formData);

      let ret;
      let submissionState: SubmissionState;

      try {
        ret = await onSubmit?.(e);
      } catch (ex) {
        ret = { error: ex };
      } finally {
        if (ret?.error) {
          e.stopImmediatePropagation();
          submissionState = 'error';
          messageRef.current =
            ret?.error?.toString?.() || 'There was unexpected error occurred. Please try again.';
        } else {
          submissionState = 'success';
          messageRef.current = ret?.message || 'Your submission was successful!';
        }

        setSubmissionState((prev) => {
          return prev !== 'pending' ? prev : submissionState;
        });

        const { component, theme, props } = getMessageType(
          submissionState,
          successMessage,
          errorMessage,
        );

        if (component === 'Notification') {
          render(
            <Notification
              {...props}
              key={Math.random()}
              theme={theme}
              onDismiss={handleDismissNotification}
              data-force-remove="true"
              open
            >
              {messageRef.current || props.children}
            </Notification>,
            getDialogContainer(),
          );
        }
      }
    },
    [
      confirmMessage,
      errorMessage,
      handleDismissConfirm,
      handleDismissNotification,
      onSubmit,
      successMessage,
    ],
  );

  const handleReset = useCallback(
    (e: TargetedEvent<HTMLFormElement>) => {
      setSubmissionState('');
      setKey(generateId()); // re-render entire form
      setFormState((prev) => ({ ...prev, defaultValues: new URLSearchParams() }));
      onReset?.(e);
    },
    [onReset],
  );

  const { isPending } = usePreviousState({ formRef, key, options: previousState });
  const [formState, setFormState] = useState<RenderProps>(() => {
    return {
      current: EMPTY_OBJECT,
      initial: EMPTY_OBJECT,
      defaultValues: new URLSearchParams(typeof location !== 'undefined' ? location.search : ''),
    };
  });

  const handleChange = useMemo(() => {
    return debounce(function (this: HTMLFormElement) {
      setFormState((prev) => {
        return {
          ...prev,
          current: getFormState(this).formDataObject,
        };
      });
    }, CHANGE_INTERVAL);
  }, []);

  useEffect(() => {
    if (formRef.current && autoSubmit && !isPending) {
      Form.submit(formRef.current);
    }
  }, [autoSubmit, key, isPending]);

  useEffect(() => {
    const { current: form } = formRef;

    if (form && !isPending) {
      const { formData, formDataObject } = getFormState(form);

      FORM_DATA_INITIAL.set(form, { formData, formDataObject });

      setFormState((prev) => {
        return {
          ...prev,
          initial: formDataObject,
          current: formDataObject,
        };
      });

      form.addEventListener('change', handleChange);
      form.addEventListener('input', handleChange);
    }

    return () => {
      form?.removeEventListener('change', handleChange);
      form?.removeEventListener('input', handleChange);
    };
  }, [handleChange, key, isPending]);

  const { component, theme, props } = getMessageType(submissionState, successMessage, errorMessage);

  return (
    <form
      method={method}
      action={action}
      onReset={handleReset}
      onSubmit={handleSubmit}
      noValidate
      data-has-submission-attempt={submissionState !== ''}
      data-url-synchronization={urlSynchronization}
      key={key}
      ref={mergedFormRef}
      {...formProps}
      data-storage-key={previousState.storageKey}
    >
      {component === 'Banner' && (
        <Banner
          {...props}
          theme={theme}
          onDismiss={submissionState === 'success' ? handleReset : undefined}
        >
          {messageRef.current || props.children}
        </Banner>
      )}
      {typeof children === 'function' ? children(formState) : children}
    </form>
  );
}

Form.getFormData = function getFormData(e: SubmitEvent) {
  return FORM_DATA.get(e) ?? (EMPTY_OBJECT as never);
};

Form.submit = function submit(form: HTMLFormElement) {
  form?.dispatchEvent(new SubmitEvent('submit', { bubbles: true, cancelable: true }));
};

const HANDLE_CHANGE_DEBOUNCE = 250;

function handleDeleteKey(e: TargetedEvent<HTMLFormElement | HTMLDialogElement>) {
  const { currentTarget } = e;
  const { storageKey } = currentTarget.dataset;

  // keep storage if confirmed or a GET form
  if (
    (e.type === 'submit' && 'method' in currentTarget && currentTarget.method === 'get') ||
    (e.type === 'close' && 'returnValue' in currentTarget && currentTarget.returnValue === 'true')
  ) {
    return;
  }

  const removeStorage = () => {
    delete localStorage[storageKey as string];
  };

  // we need to immediately remove storage...
  removeStorage();

  // ...and we also need to wait for the handleChange debounce to trigger (which re-creates _if_ the input maintained focus while reset or submit was triggered) and delete again
  setTimeout(removeStorage, HANDLE_CHANGE_DEBOUNCE);
}

function usePreviousState({
  formRef,
  key,
  options,
}: {
  formRef: MutableRef<HTMLFormElement | null>;
  key: string;
  options: NonNullable<FormProps['previousState']>;
}) {
  const { message, mode, storageKey } = options;
  const [isPending, setIsPending] = useState(true);

  const { handleDismiss: handleDismissConfirm } = Confirm.useConfirm({
    onDismiss: handleDeleteKey as ComponentProps<typeof Confirm>['onDismiss'],
  });

  useEffect(() => {
    const { current: form } = formRef;
    let dateCreated: number;

    if (!storageKey || !form) {
      setIsPending(false);
      return;
    }

    const handleChange = debounce(function (
      this: HTMLFormElement,
      e: TargetedEvent<HTMLInputElement>,
    ) {
      if ((e.target as HTMLInputElement).type === 'password') {
        return;
      }

      const form = this as HTMLFormElement;
      const formData = entriesToObject(new FormData(form));
      const now = Date.now();
      const storage = {
        dateCreated: dateCreated || now,
        dateUpdated: now,
        formData: Object.entries(formData).reduce(
          (data, [key, value]) => {
            const formField = form.elements.namedItem(key) as HTMLInputElement;
            if (formField.type !== 'password') {
              data[key] = value;
            }

            return data;
          },
          {} as Record<string, string | string[]>,
        ),
      };

      try {
        localStorage[storageKey] = JSON.stringify(storage);
      } catch {
        console.warn('Form: saving form state to localStorage failed.');
      }
    }, HANDLE_CHANGE_DEBOUNCE);

    (async () => {
      form.addEventListener('change', handleChange);
      form.addEventListener('input', handleChange);
      form.addEventListener('submit', handleDeleteKey as EventListener);
      form.addEventListener('reset', handleDeleteKey as EventListener);

      try {
        const storage = localStorage[storageKey];

        if (!storage) {
          setIsPending(false);
          return;
        }

        const parsed = JSON.parse(storage) as {
          dateCreated: number;
          dateUpdated: number;
          formData: Record<string, string | string[]>;
        };
        const { formData } = parsed;
        dateCreated = parsed.dateCreated;

        if (mode === 'confirm' && Object.values(formData).some((value) => value)) {
          render(
            <Confirm
              key={Math.random()}
              onDismiss={handleDismissConfirm}
              // eslint-disable-next-line react/no-children-prop
              children={message || 'You have unsaved changes. Do you want to apply?'}
              heading="Restore previous changes?"
              data-force-remove="true"
              data-storage-key={storageKey}
              open
            />,
            getDialogContainer(),
          );

          const shouldContinue = await new Promise((resolve) => {
            // check <dialog> returnValue to decide if onSubmit should be called
            const handler = (e: Event) => {
              const confirm = e.target as HTMLDialogElement;
              const isTrue = confirm.returnValue === 'true';

              CONFIRM_EVENTS.forEach((name) => document.removeEventListener(name, handler, true));

              resolve(isTrue);
            };

            // attach a capture event listening for the dialog close
            CONFIRM_EVENTS.forEach((name) => document.addEventListener(name, handler, true));
          });

          if (!shouldContinue) {
            setIsPending(false);
            return;
          }
        }

        const checkable = new Set(['checkbox', 'radio']);

        const setFieldState = (
          field: HTMLInputElement,
          value: string | string[],
          index: number,
        ) => {
          const arrValue = Array.isArray(value) ? value : [value];
          const parentElement = field.parentElement as HTMLElement;

          if (checkable.has(field.type)) {
            field.checked = arrValue.includes(field.value);
          } else if (field.nodeName === 'SELECT' && field.hidden) {
            const combobox = getComboBox(field.name, parentElement);
            if (combobox) {
              setComboBoxSelectedValues(combobox.input, arrValue);
            }
          } else if (field.dataset.inputMask === 'true') {
            const inputMask = getInputMask(field.name, parentElement);
            if (inputMask) {
              setInputMask(inputMask.input, arrValue[index] as string, true);
            }
          } else {
            field.value = arrValue[index] as string;
          }
        };

        Object.entries(formData).forEach(([name, value]) => {
          const field = form.elements.namedItem(name);

          if (field instanceof RadioNodeList) {
            field.forEach((f: HTMLInputElement, index) => {
              setFieldState(f, value, index);
            });
          } else if (field) {
            setFieldState(field as HTMLInputElement, value, 0);
          }
        });
      } catch {
        console.warn('Form: parsing previous form state from localStorage failed.');
        delete localStorage[storageKey];
      } finally {
        // wait a cycle for FormField state setters
        window.requestAnimationFrame(() => {
          setIsPending(false);
        });
      }
    })();

    return () => {
      form.removeEventListener('change', handleChange);
      form.removeEventListener('input', handleChange);
      form.removeEventListener('submit', handleDeleteKey as EventListener);
      form.removeEventListener('reset', handleDeleteKey as EventListener);
    };
  }, [message, mode, formRef, key, handleDismissConfirm, storageKey]);

  return { isPending };
}

function getFormState(form: HTMLFormElement, formData?: FormData) {
  return {
    get formData() {
      if (formData) {
        return formData;
      }

      let newFormData;

      // tests can be flaky even though form is always an HTMLFormElement, so fallback to empty FormData
      try {
        newFormData = new FormData(form);
      } catch {
        newFormData = new FormData();
      }

      return newFormData;
    },
    get formDataObject() {
      return entriesToObject(this.formData);
    },
    toSchema: function (
      callback?: (data: Record<string, unknown>) => Record<string, unknown>,
    ): Record<string, unknown> {
      const data = Object.entries(this.formDataObject).reduce(
        (schema, [key, value]) => {
          if (key.endsWith('[]')) {
            key = key.substring(0, key.length - 2);

            if (!Array.isArray(value)) {
              value = [value];
            }
          }

          const nested = key.split('.');
          let parsedValue: unknown = value;

          const formElement = form.elements.namedItem(key);
          // @ts-expect-error the nullish check is enough
          switch (formElement?.type) {
            case 'number':
              parsedValue = Number(value);
              break;
            case 'date':
            case 'datetime-local':
              if (value) {
                parsedValue = new Date(value as string);
              } else {
                parsedValue = null;
              }
              break;
            case 'file':
              // @ts-expect-error the switch case narrows already, but TS isn't inferring
              if (!formElement.files.length) {
                /*
                NOTE: if no files are selected, FormData creates an empty File object (e.g. { name: '', size: 0, type: 'application/octet-stream' })
                see https://html.spec.whatwg.org/multipage/form-control-infrastructure.html#constructing-form-data-set
              */
                parsedValue = undefined;
                // @ts-expect-error the switch case narrows already, but TS isn't inferring
              } else if (formElement.multiple && !Array.isArray(value)) {
                parsedValue = [value];
              }
              break;
          }

          if (nested.length > 1) {
            let root = schema;

            for (let i = 0; i < nested.length; ++i) {
              const nestedKey = nested[i] as string;

              if (i < nested.length - 1) {
                if (root[nestedKey] === undefined) {
                  root[nestedKey] = {};
                }
                root = root[nestedKey] as Record<string, unknown>;
              } else {
                root[nestedKey] = parsedValue;
              }
            }
          } else {
            schema[key] = parsedValue;
          }

          return schema;
        },
        {} as Record<string, unknown>,
      );

      if (typeof callback === 'function') {
        return callback(data);
      }

      return data;
    },
    get initialState() {
      return FORM_DATA_INITIAL.get(form);
    },
  };
}
